import React, { useCallback, useState } from 'react';
import './mobile-menu.scss';
import { ID, l } from 'app/blocks/common/codes';
import SvgIcon from 'app/blocks/common/svg-icon/svg-icon';
import LinkButtonOrLink from 'app/blocks/menu/LinkButtonOrLink';
import routes from 'app/pages/routes.json';
import ProfileLink from './profile-link';

const MenuButton = ({ isOpen, onClick, onKeyDown }) => (
    <button
        aria-controls="dropdown-menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={l(`${ID.BUTTONS}.TOP_MENU.MOBILE_MENU_ARIA_LABEL`)}
        aria-live="polite"
        className="mobileMenu-button"
        id="menu_resources_button"
        onClick={() => onClick()}
        onKeyDown={onKeyDown}
        role="menuitem"
        tabIndex={0}
        type="button"
    >
        <SvgIcon.mobileMenu height={35} width={35} />
    </button>
);

type MobileMenuProps = {
    isAuth: boolean;
    onLogout: () => void;
    goToLogin: () => void;
    dashboardURL?: string;
};

const MobileMenu: React.FC<MobileMenuProps> = ({ dashboardURL, goToLogin, isAuth, onLogout }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const handleMenuToggle = useCallback(
        (open?: boolean) => {
            setIsMenuOpen(open ?? !isMenuOpen);
        },
        [setIsMenuOpen, isMenuOpen],
    );

    const handleItemClick = useCallback(
        event => {
            if (event?.target?.tagName === 'A') {
                handleMenuToggle();
            }
        },
        [handleMenuToggle],
    );

    const handleKeyDown = useCallback(
        event => {
            if (event.key === 'Escape') {
                handleMenuToggle(false);
            }
        },
        [handleMenuToggle],
    );

    const linkTexts = {
        login: l(`${ID.BUTTONS}.TOP_MENU.LOGIN`),
        profile: l(`${ID.BUTTONS}.TOP_MENU.PROFILE`),
        register: l(`${ID.BUTTONS}.TOP_MENU.REGISTER`),
    };

    return (
        <>
            <MenuButton isOpen={isMenuOpen} onClick={handleMenuToggle} onKeyDown={handleKeyDown} />
            {isMenuOpen && (
                <>
                    <div className="mobileMenu-backdrop" />
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <div
                        aria-haspopup="menu"
                        aria-owns="menu_resources_button"
                        className="mobileMenu-container"
                        onClick={handleItemClick}
                        onKeyDown={handleKeyDown}
                    >
                        <div className="mobileMenu-header">
                            <button
                                aria-label={l(`${ID.BUTTONS}.TOP_MENU.CLOSE_MENU_ARIA_LABEL`)}
                                className="mobileMenu-closeButton"
                                onClick={() => handleMenuToggle()}
                                type="button"
                            >
                                <SvgIcon.cancel2 />
                            </button>
                        </div>
                        <ul role="menubar">
                            <li>
                                <ProfileLink goToLogin={goToLogin} isAuth={isAuth} texts={linkTexts} />
                            </li>

                            {isAuth && (
                                <li>
                                    <LinkButtonOrLink
                                        className="mobileMenu-item"
                                        data-seleniumid="menu-alerts-btn"
                                        href={routes.alerts}
                                        role="menuitem"
                                    >
                                        <SvgIcon.bell />
                                        {l(`${ID.BUTTONS}.TOP_MENU.ALERTS`)}
                                    </LinkButtonOrLink>
                                </li>
                            )}

                            <li>
                                <a
                                    className="mobileMenu-item"
                                    data-seleniumid="menu-home-btn"
                                    href={routes.root}
                                    role="menuitem"
                                >
                                    <SvgIcon.home />
                                    {l(`${ID.BUTTONS}.TOP_MENU.HOME`)}
                                </a>
                            </li>

                            <li>
                                <LinkButtonOrLink
                                    className="mobileMenu-item"
                                    data-seleniumid="menu-dashboard-btn"
                                    href={dashboardURL}
                                    role="menuitem"
                                >
                                    <SvgIcon.space_dashboard />
                                    {l(`${ID.BUTTONS}.TOP_MENU.DASHBOARD`)}
                                </LinkButtonOrLink>
                            </li>

                            <li>
                                <div className="mobileMenu-itemStatic">
                                    <SvgIcon.quick_reference />
                                    {l(`${ID.BUTTONS}.TOP_MENU.RESOURCES`)}
                                </div>
                                <div className="mobileMenu-resources" id="dropdown-menu">
                                    <a
                                        className="mobileMenu-item"
                                        data-seleniumid="menu-author-btn"
                                        href={l(`${ID.BUTTONS}.TOP_MENU.AUTHORS_URL`)}
                                        role="menuitem"
                                    >
                                        {l(`${ID.BUTTONS}.TOP_MENU.AUTHORS`)}
                                    </a>

                                    <a
                                        className="mobileMenu-item"
                                        data-seleniumid="menu-reviewer-btn"
                                        href={l(`${ID.BUTTONS}.TOP_MENU.REVIEWER_URL`)}
                                        role="menuitem"
                                    >
                                        {l(`${ID.BUTTONS}.TOP_MENU.REVIEWER`)}
                                    </a>

                                    <a
                                        className="mobileMenu-item"
                                        data-seleniumid="menu-ethic-btn"
                                        href={l(`${ID.BUTTONS}.TOP_MENU.ETHIC_GUIDELINES_URL`)}
                                        role="menuitem"
                                    >
                                        {l(`${ID.BUTTONS}.TOP_MENU.ETHIC_GUIDELINES`)}
                                    </a>
                                </div>
                            </li>

                            <li>
                                <a
                                    className="mobileMenu-item"
                                    data-seleniumid="menu-find-btn"
                                    href={l(`${ID.BUTTONS}.TOP_MENU.FIND_URL`)}
                                    role="menuitem"
                                >
                                    <SvgIcon.quick_reference_all />
                                    {l(`${ID.BUTTONS}.TOP_MENU.FIND`)}
                                </a>
                            </li>

                            {isAuth && (
                                <li>
                                    <button
                                        className="mobileMenu-item"
                                        data-seleniumid="menu-logout-btn"
                                        onClick={onLogout}
                                        role="menuitem"
                                        type="button"
                                    >
                                        <SvgIcon.logout />
                                        {l(`${ID.BUTTONS}.TOP_MENU.LOGOUT`)}
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>
                </>
            )}
        </>
    );
};

export default MobileMenu;
